/**
 * Breakpoints
 * ===========
 * Single source of truth for all JavaScript media query breakpoints.
 *
 * Values must stay in sync with SCSS _variables.scss:
 *   $breakpoint-desktop-min:   48rem  (768px)  — content layout switch
 *   $breakpoint-container-max: 80rem  (1280px) — navigation collapse point
 *
 * All MediaQueryList instances are created once at module load and shared
 * across every importer. This avoids repeatedly calling window.matchMedia()
 * and ensures addEventListener bindings are attached to the same object.
 *
 * Usage:
 *   import { CONTENT_DESKTOP_MQ, REDUCED_MOTION_MQ } from './breakpoints.js'
 *   if (CONTENT_DESKTOP_MQ.matches) { ... }
 *   CONTENT_DESKTOP_MQ.addEventListener('change', handler)
 *
 * @since 2.0.0
 */

/**
 * Raw breakpoint values in pixels.
 * Use these when you need a number for calculations, not a media query string.
 *
 * CONTENT_DESKTOP  — $breakpoint-desktop-min    — tabs, accordions, block layouts
 * NAV_DESKTOP_MIN  — $breakpoint-container-max  — navigation collapse (desktop on)
 * NAV_MOBILE_MAX   — $breakpoint-container-max - 1px — navigation mobile (desktop off)
 */
export const BREAKPOINTS = {
  CONTENT_DESKTOP: 768,
  NAV_DESKTOP_MIN: 1280,
  NAV_MOBILE_MAX: 1279,
  // Legacy aliases kept for backwards compatibility with menu-utils consumers.
  DESKTOP_MIN: 1280,
  MOBILE_MAX: 1279
}

/**
 * Content layout breakpoint (tabs, accordions, block layouts).
 * Matches $breakpoint-desktop-min (48rem = 768px).
 * Uses rem so it respects browser base font-size settings.
 */
export const CONTENT_DESKTOP_MQ = window.matchMedia('(min-width: 48rem)')

/**
 * Navigation desktop breakpoint — mega menu active.
 * Matches $breakpoint-container-max (1280px).
 */
export const NAV_DESKTOP_MQ = window.matchMedia(`(min-width: ${BREAKPOINTS.NAV_DESKTOP_MIN}px)`)

/**
 * Navigation mobile breakpoint — off-canvas menu active.
 * Matches max-width: calc($breakpoint-container-max - 1px) (1279px).
 */
export const NAV_MOBILE_MQ = window.matchMedia(`(max-width: ${BREAKPOINTS.NAV_MOBILE_MAX}px)`)

/**
 * Accessibility: user has requested reduced motion.
 * Check .matches before running animations; listen for changes if the
 * preference can change at runtime (OS setting toggle).
 */
export const REDUCED_MOTION_MQ = window.matchMedia('(prefers-reduced-motion: reduce)')
